import { useRecoilCallback } from 'recoil';
import { logger } from 'modules/logger';
import pick from 'lodash/fp/pick';
import { gqlStepToInput } from '../../../../HangingProtocol/steppedHPTypes';

import type { GetWorklistItemQuery } from 'generated/graphql';
import type { Layout, Layouts } from '../../../StudyLoader/viewerLoaderState';
import type { HangingProtocol } from '../../../../HangingProtocol/useHangingProtocol';

import { useViewportsConfigurationsSelector } from '../../../../ViewportsConfigurations/state';
import { getPayloadCaseId } from '../utils/getPayloadCaseId';
import { viewportsConfigToHangingProtocol } from '../utils/viewportsConfigToHangingProtocol';
import { useCreateHangingProtocol } from './useCreateHangingProtocol';
import { useUpdateHangingProtocol } from './useUpdateHangingProtocol';

import { useSelectedStudyDescriptions } from 'hooks/useSelectedStudyDescriptions';
import type { SubmitFormIntent } from '../constants';
import { SUBMIT_FORM_INTENT } from '../constants';
import type { ApolloCache } from '@apollo/client';
import { useBaseViewerData } from '../../../StudyLoader/useStudies';
import { WithApolloContext } from 'modules/Apollo/types';

type UseSubmitFormProps = WithApolloContext & {
  hangingProtocol: Nullable<HangingProtocol>;
  intent: SubmitFormIntent;
  currentCase: Nullable<GetWorklistItemQuery['worklistItem']>;
  shouldUpdateClassifications: boolean;
  numberOfPriors: number;
  includeLayoutChanges: boolean;
  layouts: Layouts;
  studyDescriptions: string[];
  groupedStudies: string[];
  /**
   * Called after a successful submit. `createdHangingProtocolSmid` is set only on a create whose
   * post-create match tied with an older HP, and asks the caller to pin the new HP as active.
   */
  onComplete?: (hangingProtocolName: string, createdHangingProtocolSmid?: string) => void;
  onError?: (err: Error) => void;
};

export const useSubmitForm = ({
  hangingProtocol,
  intent,
  currentCase,
  shouldUpdateClassifications,
  includeLayoutChanges,
  numberOfPriors,
  layouts,
  studyDescriptions,
  groupedStudies,
  onComplete,
  onError,
  context,
}: UseSubmitFormProps) => {
  const viewportsConfigurationsSelector = useViewportsConfigurationsSelector({ context });
  const [selectedStudyDescriptions, setStudyDescriptions] = useSelectedStudyDescriptions();
  const createHangingProtocol = useCreateHangingProtocol({ context });
  const updateHangingProtocol = useUpdateHangingProtocol({ context });

  const { invalidateAllPrecachedStudies, refetch } = useBaseViewerData({ context });

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (evt: { preventDefault: () => void; target: EventTarget | null }) => {
        evt.preventDefault();

        const viewportsConfigurations = await snapshot.getPromise(viewportsConfigurationsSelector);

        if (viewportsConfigurations == null) return;

        const fromForm = evt.target instanceof HTMLFormElement;

        const formData =
          evt.target instanceof HTMLFormElement
            ? // @ts-expect-error [EN-7967] - TS2339 - Property 'entries' does not exist on type 'FormData'. Requires DOM.Iterable in tsconfig lib.
              Object.fromEntries(new FormData(evt.target).entries())
            : ({ name: hangingProtocol?.name } as {
                name: undefined | string;
              });

        const viewportsConfigurationsPayload =
          viewportsConfigToHangingProtocol(viewportsConfigurations);

        const defaultLayoutsPayload = (Object.entries(layouts) as [string, Layout][]).map(
          ([id, layout]: [any, any]) => ({
            id,
            layout,
          })
        );

        // Preserving the existing HP layouts is an UPDATE-only behavior. On CREATE the HP has no
        // saved layouts to preserve, and a stale `hangingProtocol` prop (e.g. left over from Recoil
        // cross-window sync) must not leak the old HP's layouts into the new HP: CREATE always uses
        // the current viewer layouts. Gating on the create intent restores the original scoping.
        const isCreateIntent = intent === SUBMIT_FORM_INTENT.SAVE && fromForm;
        const shouldPreserveExistingLayouts =
          !isCreateIntent &&
          !includeLayoutChanges &&
          hangingProtocol != null &&
          hangingProtocol.data.layouts != null;

        const layoutsPayload = shouldPreserveExistingLayouts
          ? hangingProtocol.data.layouts.map((layout) => pick(['id', 'layout'], layout))
          : defaultLayoutsPayload;

        // Steps are forwarded on UPDATE only, and only after being projected onto
        // StepConfigurationInput (VX-4713).
        //
        // UPDATE has to resend them: the GraphQL layer nulls the HP's `layouts` and omits
        // `viewports` whenever `steps` is present, so dropping them here would clobber a stepped
        // HP's steps with flat layout data. CREATE is the opposite -- `hangingProtocol` is whatever
        // HP currently matches the case, so on the second create of a session these are the *first*
        // HP's steps. Copying them into a brand-new HP would both carry over another HP's step data
        // and make the GraphQL layer discard this form's own layouts/viewports. Same reasoning as
        // the layouts gating above.
        //
        // The projection matters because these steps arrive in GQL output shape, which includes
        // `order` (and `__typename` from the Apollo cache); neither exists on
        // StepConfigurationInput, and GraphQL rejects the whole `$payload` variable if either is
        // present.
        const hpSteps = isCreateIntent ? null : hangingProtocol?.steps;

        const payload = {
          name: String(formData.name),
          caseId: getPayloadCaseId(intent, currentCase, shouldUpdateClassifications),
          studyDescriptions: selectedStudyDescriptions,
          numberOfPriors,
          groupedStudies,
          data: {
            viewportsConfigurations: viewportsConfigurationsPayload,
            layouts: layoutsPayload,
          },
          ...(hpSteps != null && hpSteps.length > 0 ? { steps: hpSteps.map(gqlStepToInput) } : {}),
        };

        try {
          if (intent === SUBMIT_FORM_INTENT.SAVE && fromForm) {
            const createResult = await createHangingProtocol({
              variables: { payload },
              update(cache: ApolloCache<unknown>, { data }) {
                if (data == null) {
                  logger.warn('Unable to create hanging protocol, missing update data argument');
                  return;
                }

                if (currentCase == null) {
                  logger.warn('Unable to create hanging protocol, missing current case');
                  return;
                }

                const hangingProtocol = data.createHangingProtocol;

                if (hangingProtocol == null) {
                  logger.warn('Unable to create hanging protocol, missing createHangingProtocol');
                  return;
                }
              },
            });

            // Awaited inline rather than from `onCompleted` so the refetch result is in hand for
            // the pin decision below.
            const hangingProtocolRefetchResult = await invalidateAllPrecachedStudies();
            await refetch();

            // Ask the widget to pin the created HP only when the server's post-create match did NOT
            // land on it -- a criteria tie leaves an older same-criteria HP winning, so the widget
            // would keep showing that one and the create would look like it did nothing (VX-4713).
            // The widget already knows how to pin (`confirmCreatedHangingProtocol`); it just needs
            // the smid. Mirrors the phase-4 form, which pins under the same condition.
            const createdSmid = createResult?.data?.createHangingProtocol?.smid ?? undefined;
            const matchedSmidAfterCreate =
              hangingProtocolRefetchResult?.data?.matchHangingProtocolByWorklistsAndStudies?.smid ??
              null;
            const needsPin = createdSmid != null && matchedSmidAfterCreate !== createdSmid;

            onComplete?.(payload.name, needsPin ? createdSmid : undefined);
            return;
          }

          if (hangingProtocol == null || hangingProtocol.smid == null) {
            return;
          }

          await updateHangingProtocol({
            variables: {
              id: hangingProtocol.smid,
              payload,
            },
            update(cache: ApolloCache<unknown>, { data }) {
              if (data == null) {
                logger.warn('Unable to create hanging protocol, missing update data argument');
                return;
              }

              if (currentCase == null) {
                logger.warn('Unable to create hanging protocol, missing current case');
                return;
              }

              const hangingProtocol = data.updateHangingProtocol;

              if (hangingProtocol == null) {
                logger.warn('Unable to create hanging protocol, missing createHangingProtocol');
                return;
              }
            },
            onCompleted: async () => {
              await invalidateAllPrecachedStudies();
              await refetch();
            },
          });

          const currentDescription = currentCase?.studies[0]?.description;
          if (
            currentDescription != null &&
            !selectedStudyDescriptions.includes(currentDescription)
          ) {
            setStudyDescriptions(studyDescriptions);
          }
          onComplete?.(payload.name);
        } catch (err: any) {
          onError?.(err);
        }
      },
    [
      createHangingProtocol,
      currentCase,
      groupedStudies,
      hangingProtocol,
      includeLayoutChanges,
      intent,
      invalidateAllPrecachedStudies,
      refetch,
      layouts,
      numberOfPriors,
      onComplete,
      onError,
      selectedStudyDescriptions,
      setStudyDescriptions,
      shouldUpdateClassifications,
      studyDescriptions,
      updateHangingProtocol,
      viewportsConfigurationsSelector,
    ]
  );
};
